import {
  Component,
  effect,
  inject,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { EditorModule } from 'primeng/editor';
import { FormsModule } from '@angular/forms';
import { ButtonDirective } from 'primeng/button';
import { CustomGridStackWidget } from '../../../../../services/grid-widget-config.service';
import { WidgetStore } from '../../../../../stores/grid/widget.state';
import { StyleClassModule } from 'primeng/styleclass';
import { NgClass } from '@angular/common';
import { getTextColorBasedOnBackground } from '../../../../../utility/color.utility';
import Quill from 'quill';
import Delta from 'quill-delta';
import { GridStore } from '../../../../../stores/grid/grid.state';
import { getState } from '@ngrx/signals';
import { DebounceInputDirective } from '../../../../../directives/debounce-input.directive';
import { TooltipModule } from 'primeng/tooltip';
import { debounceTime, Subject } from 'rxjs';

@Component({
  selector: 'app-text',
  standalone: true,
  imports: [
    EditorModule,
    FormsModule,
    ButtonDirective,
    StyleClassModule,
    NgClass,
    DebounceInputDirective,
    TooltipModule,
  ],
  templateUrl: './text.component.html',
  styleUrl: './text.component.scss',
})
export class TextComponent implements OnInit, OnDestroy {
  @ViewChild('quillContainer', { static: true }) quillContainer!: any;

  readonly gridStore = inject(GridStore);
  readonly widgetStore = inject(WidgetStore);
  @Input() id!: string;

  private contentChangeSubject = new Subject<string>(); // Subject for content changes
  private contentChangeSubscription: any;

  config: CustomGridStackWidget | null = null;
  previewMode = false;
  text = '';
  backgroundColor = '';

  quill: Quill | null = null; // Quill instance
  public htmlContent = ''; // Bindable editor content

  public fontAwesomeIcons: { class: string; label: string }[] = [
    { class: 'fas fa-plus-circle', label: 'Plus Circle' },
    { class: 'fas fa-check-circle', label: 'Check Circle' },
    { class: 'fas fa-ban', label: 'Ban' },
  ];

  constructor() {
    effect(() => {
      const gridState = getState(this.gridStore);
      this.config = this.widgetStore.get(this.id);
      this.backgroundColor =
        this.config?.widget?.text?.backgroundColor || '#ffffff';

      if (gridState.readonly) {
        this.previewMode = true;
      }
    });

    //this.registerFontAwesomeBlot(); // Register blot before editor runs
  }

  quillModules2 = {
    clipboard: {
      matchVisual: false, // Ensure content paste doesn't strip attributes
      matchers: [
        [
          'I', // Matcher for <i> tags
          (node: any, delta: any) => {
            const className = node.getAttribute('class'); // Check `class` attribute
            if (className && className.includes('fa')) {
              // FontAwesome class processing
              return delta.compose(
                new Delta().insert(node.innerText || '', {
                  fontawesome: className,
                })
              );
            } else {
              // Standard <i> processing (italic)
              return delta.compose(
                new Delta().insert(node.innerText || '', { italic: true })
              );
            }
          },
        ],
      ],
    },
  };

  /**
   * Lifecycle hook that is called after data-bound properties of a directive are initialized.
   * Performs necessary initializations including widget configuration and subscription setup.
   *
   * @return {void} This method does not return a value.
   */
  ngOnInit(): void {
    this.config = this.widgetStore.get(this.id);
    this.backgroundColor =
      this.config?.widget?.text?.backgroundColor || '#ffffff';

    // Your existing initialization logic...
    this.contentChangeSubscription = this.contentChangeSubject
      .pipe(debounceTime(300)) // Adjust debounce time as needed
      .subscribe(() => this.saveToConfig());
  }

  /**
   * Handles content changes, processes the given content to replace non-breaking spaces
   * with regular spaces, and updates the HTML content accordingly.
   *
   * @param {string} content - The content string that may contain non-breaking spaces to be replaced.
   * @return {void} This method does not return a value.
   */
  onContentChange(content: string): void {
    // Check if there are any non-breaking spaces
    if (content && content.indexOf('&nbsp;') > -1) {
      // Replace all &nbsp; with regular spaces
      this.htmlContent = content.replace(/&nbsp;/g, ' ');
    } else {
      this.htmlContent = content; // Keep content as is
    }

    this.saveToConfig();
  }

  /**
   * Saves the current widget configuration to the config object.
   * Updates the widget's text content or initializes it if it doesn't exist,
   * then updates the widget store and triggers layout changes in the grid store.
   *
   * @return {void} This method does not return a value.
   */
  saveToConfig(): void {
    if (!this.config?.widget) return;
    if (!this.config.widget.text) {
      this.config.widget.text = {
        content: this.htmlContent,
        alignment: null,
        backgroundColor: '#ffffff',
      };
    } else {
      this.config.widget.text.content = this.htmlContent;
    }

    this.widgetStore.update(this.id, this.config);
    this.gridStore.triggerLayoutChanges();
  }

  /**
   * Initializes the editor with the provided event and text configuration.
   *
   * @param {any} event - The event object containing the editor instance.
   * @param {any} textConf - The configuration object containing text content.
   * @return {void} Does not return a value.
   */
  initEditor(event: any, textConf: any): void {
    this.quill = event.editor;
    if (!this.quill) return;
    this.htmlContent = textConf.content;
  }

  quillModules = {
    clipboard: {
      matchVisual: false, // Prevent content cleanup on paste
    },
    //formats: ['customIcon', 'italic'], // Ensure `customIcon` is included
  };

  registerFontAwesomeBlot() {
    const InlineBlot = Quill.import('blots/inline') as any;

    class IconBlot extends InlineBlot {
      static blotName = 'customIcon'; // Name used in Delta for custom rendering
      static tagName = 'i'; // HTML tag <i> for FontAwesome icons

      // Create the <i> element with the appropriate FontAwesome class
      static create(value: string): HTMLElement {
        const node = super.create();
        node.setAttribute('class', value); // Add FontAwesome class
        return node;
      }

      // Extract the class name from the <i> element for Delta storage
      static value(node: HTMLElement): string {
        return node.getAttribute('class')!;
      }
    }

    // Register the custom blot with Quill
    Quill.register(IconBlot);
  }

  addIcon() {
    if (!this.quill) return;

    const range = this.quill.getSelection() || {
      index: this.quill.getLength(),
    };
    const position = range.index;

    const icon = `<i class="fas fa-plus-circle"></i>`;
    const newHtml =
      this.htmlContent.substring(0, position) +
      icon +
      this.htmlContent.substring(position + 2, this.quill.getLength() + 2);

    setTimeout(() => {
      this.htmlContent = newHtml;
    });
    // Clear block formats (e.g., list) to avoid conflicts
    //this.quill.format('list', false);
    //
    //// Insert the custom icon
    //this.quill.insertEmbed(
    //  position,
    //  'customIcon',
    //  'fas fa-plus-circle',
    //  'user'
    //);
    //this.quill.insertText(position + 1, ' ', 'user'); // Add space after icon
    //this.quill.insertText(position + 1, ' ', 'user'); // Add space after icon
    //this.quill.setSelection(position + 1);
  }

  ngOnDestroy(): void {
    // Make sure to clean up
    this.quill = null;
  }

  getCssClass(): string {
    const color = getTextColorBasedOnBackground();
    const alignment = this.getAlignment();
    return color + ' ' + alignment;
  }

  /**
   * Retrieves the alignment setting for the widget's text and generates a corresponding class name.
   *
   * @return {string} A string representing the alignment class name. If no alignment is configured, an empty string is returned.
   */
  getAlignment(): string {
    const align = this.config?.widget?.text?.alignment;
    return align ? 'border-arrow--' + align : '';
  }

  /**
   * Toggles the preview mode state by switching its boolean value.
   *
   * @return {void} Does not return any value.
   */
  togglePreview(): void {
    this.previewMode = !this.previewMode;
  }

  createIconDelta(iconClass: string): Delta {
    return new Delta()
      .insert(' ') // Before the icon
      .insert({ customIcon: iconClass }) // Custom icon
      .insert(' '); // After the icon
  }

  protected readonly getTextColorBasedOnBackground =
    getTextColorBasedOnBackground;
}
